import { useState, useEffect, useRef } from 'react';
import { Check, ChevronDown, Droplet, Filter } from 'lucide-react';
import BlurOverlay from '../ui/BlurOverlay';
import { generateRecommendations } from "@/utils/oilRecommendationUtils";
import { getOilCapacityForCar } from "@/utils/oilCapacityUtils";
import SummaryBox from './SummaryBox';
// Import car data split out for clarity
import { carMakes, carLogos, carModels, years } from '@/utils/carData';

// Engine types in user-specified order
export const engineTypes = [
  'Diesel',
  'Gasoline',
  'Hybrid',
  '4-cylinder',
  '6-cylinder',
  '8-cylinder',
  '1.0',
  '1.1',
  '1.2',
  '1.3',
  '1.4',
  '1.5',
  '1.6',
  '1.7',
  '1.8',
  '1.9',
  '2.0'
];

type CarModels = {
  [key: string]: string[];
};
interface CarFormProps {
  onSubmit?: (data: any) => void;
}

const CarForm = ({
  onSubmit
}: CarFormProps) => {
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  // Engine type as array for multi-select
  const [engineType, setEngineType] = useState<string[]>([]);
  const [primaryOilProduct, setPrimaryOilProduct] = useState<{
    name: string;
    viscosity?: string;
    description?: string;
  } | null>(null);
  const [recommendedFilter, setRecommendedFilter] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Add: litersNeeded and original auto capacity to track user's override
  const [litersNeeded, setLitersNeeded] = useState(4 as number | "");
  const [autoLiters, setAutoLiters] = useState<number>(4);
  // To track if user has overridden liters field
  const [litersEdited, setLitersEdited] = useState(false);
  const [makeDropdownOpen, setMakeDropdownOpen] = useState(false);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  // Don't need engineDropdownOpen for new UI, but keep for legacy code
  const [engineDropdownOpen, setEngineDropdownOpen] = useState(false);
  // Engine checkbox dropdown for multi-select
  const [engineCheckboxOpen, setEngineCheckboxOpen] = useState(false);

  // When car details change, reset litersNeeded to suggested capacity… unless user has overridden it.
  useEffect(() => {
    if (make && model && year && engineType.length > 0) {
      // Use first engine type for summary and recommendations for now
      const mainEngine = engineType[0];
      // New logic: get default capacity for car
      const capacity = getOilCapacityForCar(make, year, mainEngine);
      setAutoLiters(capacity);
      if (!litersEdited) setLitersNeeded(capacity);
      const data = {
        make,
        model,
        year,
        engineType: engineType.join(", ")
      };
      const recs = generateRecommendations(data);
      if (recs.length > 0) {
        setPrimaryOilProduct({
          name: recs[0].oilType,
          viscosity: recs[0].viscosity,
          description: recs[0].description
        });
      } else {
        setPrimaryOilProduct(null);
      }
      let filterType = 'Premium Oil Filter';
      const yearNum = parseInt(year);
      if (['BMW', 'Mercedes-Benz', 'Audi', 'Porsche', 'Lexus', 'Infiniti'].includes(make)) {
        filterType = 'OEM Premium Filter';
      } else if (yearNum >= 2015) {
        filterType = 'Extended Life Filter';
      } else if (yearNum < 2000) {
        filterType = 'Standard Oil Filter';
      }
      setRecommendedFilter(filterType);
    } else {
      setPrimaryOilProduct(null);
      setRecommendedFilter('');
      setAutoLiters(4);
      if (!litersEdited) setLitersNeeded(4);
    }
    // eslint-disable-next-line
  }, [make, model, year, engineType]);

  // If all fields are cleared, also reset manual edit flag
  useEffect(() => {
    if (!make && !model && !year && engineType.length === 0 && litersEdited) {
      setLitersEdited(false);
    }
  }, [make, model, year, engineType, litersEdited]);

  const handleLitersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLitersEdited(true);
    // Accept only valid numbers greater than 0, with optional decimals
    if (val === "") {
      setLitersNeeded("");
      return;
    }
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0) {
      setLitersNeeded(Number(num));
    }
  };
  const handleLitersBlur = () => {
    // Reset to auto if value blanked out
    if (litersNeeded === "") {
      setLitersNeeded(autoLiters);
      setLitersEdited(false);
    }
  };

  // Multi-select handling for engine types
  const toggleEngineType = (type: string) => {
    setEngineType(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);
    if (onSubmit) {
      onSubmit({
        make,
        model,
        year,
        // Send all selected engine types (array), as comma-separated string for legacy compatibility
        engineType: engineType.join(", "),
        engineTypes: engineType, // Also provide as array for any forward compatibility
        recommendedOil: primaryOilProduct?.name ?? '',
        recommendedFilter,
        litersNeeded: Number(litersNeeded) || autoLiters
      });
    }
  };

  return (
    <BlurOverlay className="max-w-4xl mx-auto">
      <div className="flex flex-col lg:flex-row items-center space-y-6 lg:space-y-0 lg:space-x-8">
        <div className="w-full lg:w-5/12">
          <h3 className="text-2xl font-bold text-autocare-blue mb-2">Find Your Perfect Oil</h3>
          <p className="text-gray-600 mb-4">
            Enter your vehicle details and we'll recommend the best oil type and filter for your car.
          </p>
          <div className="relative pt-4 hidden lg:block space-y-4">
            <SummaryBox icon={<Droplet className="h-8 w-8 text-autocare-blue" />} label="Recommended Oil" value={formSubmitted && primaryOilProduct ? <>
                    {primaryOilProduct.name}
                    {primaryOilProduct.viscosity && <span className="ml-2 text-autocare-blue text-sm">
                        {primaryOilProduct.viscosity}
                      </span>}
                  </> : "Waiting for your input..."} />
            {/* Removed the "Recommended Filter" summary box as requested */}
            <SummaryBox icon={<span className="flex items-center justify-center h-8 w-8 text-autocare-blue font-bold text-2xl">
                  L
                </span>} label="Required Liters" value={formSubmitted && litersNeeded ? litersNeeded : "Waiting for your input..."} />
          </div>
        </div>
        <div className="w-full lg:w-7/12">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Car Make Dropdown */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Car Make</label>
              <button type="button" className="input-field text-left flex items-center justify-between" onClick={() => setMakeDropdownOpen(!makeDropdownOpen)}>
                <div className="flex items-center">
                  {make && carLogos[make] && <img src={carLogos[make]} alt={`${make} logo`} className="h-6 w-6 mr-2 object-contain" />}
                  <span>{make || 'Select Car Make'}</span>
                </div>
                <ChevronDown className="h-5 w-5 text-gray-400" />
              </button>
              {makeDropdownOpen && <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 overflow-auto border border-gray-200">
                  {carMakes.map(item => <div key={item} className="px-4 py-2 hover:bg-autocare-gray-light cursor-pointer flex items-center justify-between" onClick={() => {
                setMake(item);
                setModel('');
                setMakeDropdownOpen(false);
              }}>
                      <div className="flex items-center">
                        {carLogos[item] && <img src={carLogos[item]} alt={`${item} logo`} className="h-6 w-6 mr-2 object-contain" />}
                        <span>{item}</span>
                      </div>
                      {make === item && <Check className="h-4 w-4 text-autocare-blue" />}
                    </div>)}
                </div>}
            </div>
            {/* Car Model Dropdown */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Car Model</label>
              <button type="button" className={`input-field text-left flex items-center justify-between ${!make ? 'bg-gray-100 cursor-not-allowed' : ''}`} onClick={() => make && setModelDropdownOpen(!modelDropdownOpen)} disabled={!make}>
                <span>{model || 'Select Car Model'}</span>
                <ChevronDown className="h-5 w-5 text-gray-400" />
              </button>
              {modelDropdownOpen && <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 overflow-auto border border-gray-200">
                  {(carModels as CarModels)[make]?.map(item => <div key={item} className="px-4 py-2 hover:bg-autocare-gray-light cursor-pointer flex items-center justify-between" onClick={() => {
                setModel(item);
                setModelDropdownOpen(false);
              }}>
                      {item}
                      {model === item && <Check className="h-4 w-4 text-autocare-blue" />}
                    </div>)}
                </div>}
            </div>
            {/* Year Dropdown */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <button type="button" className="input-field text-left flex items-center justify-between" onClick={() => setYearDropdownOpen(!yearDropdownOpen)}>
                <span>{year || 'Select Year'}</span>
                <ChevronDown className="h-5 w-5 text-gray-400" />
              </button>
              {yearDropdownOpen && <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 overflow-auto border border-gray-200">
                  {years.map(item => <div key={item} className="px-4 py-2 hover:bg-autocare-gray-light cursor-pointer flex items-center justify-between" onClick={() => {
                setYear(item.toString());
                setYearDropdownOpen(false);
              }}>
                      {item}
                      {year === item.toString() && <Check className="h-4 w-4 text-autocare-blue" />}
                    </div>)}
                </div>}
            </div>
            {/* Engine type Multi-Select */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Engine Type(s)</label>
              <button
                type="button"
                className={`input-field text-left flex items-center justify-between`}
                onClick={() => setEngineCheckboxOpen(!engineCheckboxOpen)}
              >
                <span>
                  {engineType.length > 0
                    ? engineType.join(", ")
                    : "Select Engine Type(s)"}
                </span>
                <ChevronDown className="h-5 w-5 text-gray-400" />
              </button>
              {engineCheckboxOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 overflow-auto border border-gray-200">
                  {engineTypes.map(item => (
                    <div
                      key={item}
                      className="px-4 py-2 hover:bg-autocare-gray-light cursor-pointer flex items-center"
                      onClick={e => {
                        // prevent close, allow click/selection
                        e.stopPropagation();
                        toggleEngineType(item);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={engineType.includes(item)}
                        readOnly
                        className="accent-autocare-blue mr-2"
                        tabIndex={-1}
                      />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* The Required Oil Quantity (Liters) input has been removed. */}
            <button type="submit" className="btn-primary w-full mt-6">
              Find Recommended Oil
            </button>
          </form>
        </div>
      </div>
    </BlurOverlay>
  );
};
export default CarForm;
